import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = movies.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setActive((v) => (v + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (dir: number) => setActive((v) => (v + dir + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const current = movies[active];

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-primary-950 md:h-[600px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      aria-label="影视焦点轮播"
    >
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={movie.backdrop || movie.poster}
            alt={`《${movie.title}》剧照`}
            title={`${movie.title} 秋霞影视`}
            className={`h-full w-full object-cover object-top ${
              index === active ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/30"></div>
        </div>
      ))}

      <div className="relative mx-auto flex h-full max-w-site flex-col justify-end px-4 pb-16 md:px-6 md:pb-20">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-600 text-primary-950">
              {current.type}
            </span>
            <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
              <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
              {current.rating.toFixed(1)}
            </span>
            <span className="text-xs text-background-100/80">
              {current.year} · {current.region} · {current.genres.join(" / ")}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-700 leading-tight text-background-50 md:text-5xl">
            {current.title}
          </h1>
          <p className="mt-1 text-sm text-background-100/70">{current.alias}</p>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
            {current.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-600 text-primary-950 transition-colors hover:bg-accent-400 whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg" aria-hidden="true"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="flex items-center gap-2 rounded-md border border-background-50/30 bg-background-50/10 px-6 py-3 text-sm text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 whitespace-nowrap"
            >
              更多{current.type}
            </Link>
          </div>
        </div>
      </div>

      {/* controls */}
      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:flex cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:flex cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 md:bottom-8">
        {movies.map((movie, index) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/40"
            }`}
            aria-label={`切换到第${index + 1}张`}
          ></button>
        ))}
      </div>
    </section>
  );
}